import { useId } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import useAuth from 'src/hooks/useAuth';
import Button from 'src/componentes/button';
import { capitalizeFirstLetter } from 'src/utils/string';
import {
  BadgeMatrizRisco,
  CelulaMatrizRisco,
  obterClasseCor,
  obterCorValida,
} from './TabelaMatrizRiscoLayout';
import {
  agruparCelulasPorGrau,
  obterAvaliacoesGrau,
  obterPosicaoCelula,
  quadranteContemSelecionados,
} from './tabela.utils';

export const VISAO_MATRIZ = {
  QUANTIDADE: 'quantidade',
  GRAU_RISCO: 'grauRisco',
};

export const obterGrausLegenda = ({ grauMatrizRisco, itens } = {}) => {
  const graus = grauMatrizRisco?.length
    ? grauMatrizRisco.map(({ grauRisco }) => grauRisco)
    : (itens ?? [])
        .map(({ grauRisco }) => grauRisco)
        .sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

  return graus.filter(
    (grau, index) =>
      grau?.id != null &&
      graus.findIndex((outro) => outro?.id === grau.id) === index
  );
};

const LegendaGrausMatriz = ({ graus }) => {
  const intl = useIntl();
  if (!graus.length) return null;

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-1">
      <span className="tw:text-xs tw:font-semibold tw:tracking-wider tw:text-muted-foreground tw:uppercase">
        {intl.formatMessage({ id: 'labelLegenda' })}
      </span>
      <ul className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-1 tw:p-0 tw:text-sm">
        {graus.map((grau) => (
          <li key={grau.id} className="tw:flex tw:items-center tw:gap-1.5">
            <span
              aria-hidden
              className={clsx(
                'tw:size-3 tw:shrink-0 tw:rounded-full',
                obterClasseCor(obterCorValida(grau.cor))
              )}
            />
            {grau.descricao}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const BarraVisaoMatriz = ({ visao, onChange, disabled, graus }) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const labelId = useId();
  const opcoes = [
    {
      valor: VISAO_MATRIZ.QUANTIDADE,
      label: intl.formatMessage({ id: 'quantidade' }),
    },
    {
      valor: VISAO_MATRIZ.GRAU_RISCO,
      label: capitalizeFirstLetter(
        intl.formatMessage({ id: 'grauRisco' }, { risco: resources.risco })
      ),
    },
  ];

  return (
    <div className="tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-6 tw:gap-y-3">
      <div className="tw:flex tw:items-center tw:gap-3">
        <span
          id={labelId}
          className="tw:text-sm tw:font-medium tw:text-muted-foreground"
        >
          {intl.formatMessage({ id: 'labelVisualizarMatrizPor' })}
        </span>
        <div role="group" aria-labelledby={labelId} className="tw:inline-flex">
          {opcoes.map(({ valor, label }) => (
            <Button
              key={valor}
              type="button"
              color={visao === valor ? 'primary' : 'secondary'}
              aria-pressed={visao === valor}
              disabled={disabled}
              onClick={() => visao !== valor && onChange(valor)}
              className="tw:rounded-none tw:first:rounded-l-md tw:last:rounded-r-md"
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <LegendaGrausMatriz graus={graus} />
    </div>
  );
};

const BORDAS = {
  topo: {
    interna: 'tw:border-t tw:border-t-black/15',
    grau: 'tw:border-t-2 tw:border-t-white',
    selecao: 'tw:border-t-2 tw:border-t-black',
  },
  esquerda: {
    interna: 'tw:border-l tw:border-l-black/15',
    grau: 'tw:border-l-2 tw:border-l-white',
    selecao: 'tw:border-l-2 tw:border-l-black',
  },
};

export const criarRenderCelulaGrau = ({
  linhas,
  colunas,
  obterQuadrante,
  riscoAvaliacoes,
  riscoAvaliacoesOriginal,
  idsSelecionados,
  inerente,
  setFilterGrau,
  setPositionMouse,
  grauEmFoco,
  setGrauEmFoco,
}) => {
  const quadrantes = linhas.map((linha) =>
    colunas.map((coluna) => obterQuadrante(linha, coluna))
  );
  const chaves = quadrantes.map((linha) =>
    linha.map((quadrante) => quadrante?.chaveGrau ?? null)
  );
  const possuiSelecao = idsSelecionados?.length > 0;
  const resumos = new Map();

  agruparCelulasPorGrau(chaves).forEach(
    ({ celulas, posicaoTotalizador }, chave) => {
      const quadrantesGrau = celulas.map(
        ([indexLinha, indexColuna]) => quadrantes[indexLinha][indexColuna]
      );
      const avaliacoes = obterAvaliacoesGrau(
        riscoAvaliacoes,
        quadrantesGrau,
        inerente
      );
      const avaliacoesOriginais =
        riscoAvaliacoesOriginal && riscoAvaliacoesOriginal !== riscoAvaliacoes
          ? obterAvaliacoesGrau(
              riscoAvaliacoesOriginal,
              quadrantesGrau,
              inerente
            )
          : avaliacoes;
      resumos.set(chave, {
        avaliacoes,
        ids: avaliacoesOriginais.map(({ risco }) => risco.id),
        destacado: quadranteContemSelecionados(avaliacoes, idsSelecionados),
        posicaoTotalizador,
      });
    }
  );

  const obterBorda = (lado, chave, chaveVizinha) => {
    if (chaveVizinha === undefined) return null;
    if (chaveVizinha === chave) return BORDAS[lado].interna;
    const selecionada =
      possuiSelecao &&
      (resumos.get(chave)?.destacado || resumos.get(chaveVizinha)?.destacado);
    return selecionada ? BORDAS[lado].selecao : BORDAS[lado].grau;
  };

  return (linha, coluna, indexColuna) => {
    const indexLinha = linhas.indexOf(linha);
    const quadrante = quadrantes[indexLinha][indexColuna];
    const key = linha.id + '-' + coluna.id;
    const chave = chaves[indexLinha][indexColuna];
    const resumo = resumos.get(chave);
    if (!quadrante || !resumo) {
      return <td key={key} className="tw:border tw:border-border tw:p-0" />;
    }

    return (
      <CelulaMatrizRisco
        key={key}
        agrupada
        cor={quadrante.cor}
        selecionada={possuiSelecao && resumo.destacado}
        emFoco={grauEmFoco === chave}
        desbotada={!resumo.destacado}
        label={linha.nome + ' / ' + coluna.nome}
        className={[
          'tw:border-dashed',
          obterBorda('topo', chave, chaves[indexLinha - 1]?.[indexColuna]),
          obterBorda('esquerda', chave, chaves[indexLinha][indexColuna - 1]),
        ]}
        onClick={() => setFilterGrau?.(resumo.ids, chave, inerente)}
        onMouseEnter={() => {
          setPositionMouse?.(indexColuna, inerente);
          setGrauEmFoco?.(chave);
        }}
        onMouseLeave={() => {
          setPositionMouse?.(-1);
          setGrauEmFoco?.(null);
        }}
      >
        {resumo.posicaoTotalizador ===
          obterPosicaoCelula(indexLinha, indexColuna) && (
          <BadgeMatrizRisco>{resumo.avaliacoes.length}</BadgeMatrizRisco>
        )}
      </CelulaMatrizRisco>
    );
  };
};
